import { useId } from 'react'
import type { ReactNode, Ref } from 'react'
import { Box, Dialog, IconButton, Typography, useMediaQuery } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import type { Breakpoint } from '@mui/material/styles'
import { Close as CloseIcon } from '@mui/icons-material'

import { colors, scrollbarSx } from '../../theme'

/**
 * One anatomy for every dialog on the Opportunities page: a fixed header, a
 * body that is the only scroll region, and a fixed footer.
 *
 * The pieces are exported on their own because a right-hand panel (a Drawer)
 * wants the same header, body and footer but must keep its own container.
 *
 * Phone behaviour:
 * - A dialog with a form or a record becomes a full-screen sheet below `sm`.
 * - A `compact` dialog (a confirmation or a one- or two-field prompt) becomes a
 *   bottom sheet instead, so it does not cover a screen it has no content for.
 *
 * The explicit paper colour is not redundant. MuiDrawer's theme override paints
 * paper with gray900 for the navigation rail, and a content surface that
 * inherits it renders unreadable dark-on-dark. Anything rendering its own
 * paper states its own colour.
 */

const PHONE_PX = { xs: 2, sm: 2.5 }

/** Width of the right-hand reference rail in a `SplitBody`. */
const RAIL_PX = 296

/**
 * Field labels sit above the control instead of floating inside the outline.
 * Stated once here, on the body every dialog shares, so the fields themselves
 * keep their ordinary `label` prop (and the accessible name and required
 * asterisk that come with it). The notch the floating label would have cut into
 * the outline is hidden, since the label no longer lives there.
 */
const fieldsAboveSx = {
  '& .MuiInputLabel-root': {
    position: 'static',
    transform: 'none',
    maxWidth: '100%',
    pointerEvents: 'auto',
    marginBottom: '6px',
    fontSize: '0.75rem',
    lineHeight: '1rem',
    fontWeight: 600,
    color: 'text.primary',
    whiteSpace: 'normal',
    '&.Mui-focused, &.Mui-error, &.Mui-disabled': { color: 'text.primary' },
  },
  '& .MuiInputLabel-root .MuiFormLabel-asterisk': { color: 'error.dark' },
  '& .MuiOutlinedInput-notchedOutline': { top: 0 },
  '& .MuiOutlinedInput-notchedOutline legend': { display: 'none' },
  '& .MuiOutlinedInput-input': { padding: '8px 10px' },
  '& .MuiInputBase-sizeSmall .MuiOutlinedInput-input': { padding: '5px 10px' },
  '& .MuiInputBase-multiline.MuiOutlinedInput-root': { padding: 0 },
  '& .MuiOutlinedInput-root .MuiSelect-select': { paddingRight: '32px' },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root': { padding: '0 10px' },
  '& .MuiAutocomplete-root .MuiOutlinedInput-root .MuiAutocomplete-input': { padding: '8px 4px 8px 0' },
  '& .MuiAutocomplete-root .MuiInputBase-sizeSmall .MuiAutocomplete-input': { padding: '5px 4px 5px 0' },
  '& .MuiFormHelperText-root': { fontWeight: 400, marginLeft: 0, marginRight: 0, marginTop: '6px' },
} as const

export function ShellHeader({
  overline,
  title,
  subtitle,
  extra,
  extraOwnRowOnPhone,
  onClose,
  closeLabel = 'Close',
  titleId,
  mono,
}: {
  /** A line above the title, outside the dialog's accessible name. */
  overline?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  /** A status chip, a Select or a step indicator that sits beside the close button. */
  extra?: ReactNode
  /** Move `extra` to its own row below the title on a phone, for controls too wide to share one. */
  extraOwnRowOnPhone?: boolean
  onClose: () => void
  closeLabel?: string
  titleId?: string
  /** A tile at the left edge that says what the record is: initials, an icon. */
  mono?: ReactNode
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        columnGap: 1.5,
        rowGap: 1,
        px: PHONE_PX,
        pt: 2.25,
        pb: 2,
        borderBottom: 1,
        borderColor: 'divider',
        flexShrink: 0,
      }}
    >
      {mono && <Box sx={{ flexShrink: 0, mt: 0.125 }}>{mono}</Box>}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {overline && (
          <Typography variant="caption" color="text.secondary" component="div" sx={{ fontWeight: 400, mb: 0.25 }}>
            {overline}
          </Typography>
        )}
        <Typography
          variant="h5"
          component="h2"
          id={titleId}
          sx={{ overflowWrap: 'anywhere', color: 'text.primary' }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      {extra && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexShrink: 0,
            ...(extraOwnRowOnPhone && {
              order: { xs: 3, sm: 0 },
              flexBasis: { xs: '100%', sm: 'auto' },
              '& > *': { flex: { xs: 1, sm: 'none' } },
            }),
          }}
        >
          {extra}
        </Box>
      )}
      <IconButton
        aria-label={closeLabel}
        onClick={onClose}
        sx={{ width: { xs: 44, sm: 36 }, height: { xs: 44, sm: 36 }, mt: -0.5, mr: -0.75, flexShrink: 0 }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  )
}

const bodyTouchSx = {
  // A touch target is 44px. Body buttons are mostly size="small" for the
  // desktop density, so the floor is stated once here for every dialog.
  '@media (max-width:767.95px)': {
    '& .MuiButton-root': { minHeight: 44 },
    '& .MuiIconButton-root:not(.MuiAutocomplete-popupIndicator):not(.MuiAutocomplete-clearIndicator)': {
      minWidth: 44,
      minHeight: 44,
    },
  },
} as const

export function ShellBody({
  children,
  bodyRef,
}: {
  children: ReactNode
  bodyRef?: Ref<HTMLDivElement>
}) {
  return (
    <Box
      ref={bodyRef}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        px: PHONE_PX,
        pt: 2,
        pb: 2.5,
        ...scrollbarSx,
        ...fieldsAboveSx,
        ...bodyTouchSx,
      }}
    >
      {children}
    </Box>
  )
}

/**
 * A body with a working column and a tinted reference rail. The rail sits to
 * the right from 880px and stacks under the main column below that, in the same
 * reading order, so a phone loses nothing. The whole body is one scroll region;
 * the grid row stretches so the rail's tint runs to the footer even when the
 * main column is short.
 */
export function SplitBody({
  main,
  rail,
  bodyRef,
}: {
  main: ReactNode
  rail: ReactNode
  bodyRef?: Ref<HTMLDivElement>
}) {
  return (
    <Box
      ref={bodyRef}
      sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'grid',
        gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: `minmax(0, 1fr) ${RAIL_PX}px` },
        // The rail's tint and its left rule are painted on the scroll container
        // itself, attached to the content, so they run the full height of the
        // scrollable area however long the main column is. A grid row cannot do
        // this: with a definite container height it never grows past 100%.
        backgroundAttachment: 'local',
        backgroundImage: {
          md: `linear-gradient(to right, transparent calc(100% - ${RAIL_PX + 1}px), ${colors.gray200} calc(100% - ${RAIL_PX + 1}px), ${colors.gray200} calc(100% - ${RAIL_PX}px), ${colors.gray50} calc(100% - ${RAIL_PX}px))`,
        },
        ...scrollbarSx,
        ...fieldsAboveSx,
        ...bodyTouchSx,
      }}
    >
      <Box sx={{ minWidth: 0, px: PHONE_PX, pt: 2.5, pb: 2.5 }}>{main}</Box>
      <Box
        component="aside"
        sx={{
          minWidth: 0,
          px: 2.5,
          pt: 2.5,
          pb: 2.5,
          backgroundColor: { xs: 'background.default', md: 'transparent' },
          // Explicit colours: a responsive border shorthand resets the colour
          // to currentColor if a separate borderColor precedes it.
          borderTop: { xs: `1px solid ${colors.gray200}`, md: 0 },
        }}
      >
        {rail}
      </Box>
    </Box>
  )
}

export function ShellFooter({
  children,
  start,
}: {
  children?: ReactNode
  /** A destructive control or a message that sits at the left edge, apart from the primary actions. */
  start?: ReactNode
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 1.25,
        px: PHONE_PX,
        py: 1.5,
        borderTop: 1,
        borderColor: 'divider',
        backgroundColor: 'background.default',
        flexShrink: 0,
        '& > .MuiButton-root': { minHeight: { xs: 44, sm: 36 }, flex: { xs: 1, sm: 'none' } },
        // Buttons in the start slot are not direct children, so they need the
        // phone touch-target floor stated again.
        '& .MuiButton-root': { minHeight: { xs: 44, sm: 36 } },
        // White on error.main is 3.8:1; error.dark is 4.8:1 and is already in the theme.
        '& .MuiButton-containedError': {
          backgroundColor: 'error.dark',
          '&:hover': { backgroundColor: 'error.dark', filter: 'brightness(0.92)' },
        },
      }}
    >
      {start && <Box sx={{ mr: 'auto', flex: { xs: '1 1 100%', sm: '1 1 220px' }, minWidth: 0 }}>{start}</Box>}
      {children}
    </Box>
  )
}

export interface DialogShellProps {
  open: boolean
  onClose: () => void
  overline?: ReactNode
  title: ReactNode
  subtitle?: ReactNode
  extra?: ReactNode
  extraOwnRowOnPhone?: boolean
  /** A tile at the left of the header that says what the record is. */
  mono?: ReactNode
  /** Fixed row between the header and the body, for tabs or a jump-to-section strip. */
  belowHeader?: ReactNode
  /**
   * A message that must stay in view whatever the body has scrolled to, such as
   * the reason the footer's primary action is disabled. Sits between the header
   * and the body, outside the scroll region.
   */
  dock?: ReactNode
  children?: ReactNode
  footer?: ReactNode
  footerStart?: ReactNode
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg'
  /** A pixel width, for a form wider than `sm` and narrower than `md`. Overrides `maxWidth`. */
  width?: number
  /** A confirmation or short prompt: a bottom sheet on a phone instead of a full screen. */
  compact?: boolean
  closeLabel?: string
  /** Where the dialog turns into a sheet: a breakpoint key or a pixel width. Defaults to `sm`. */
  fullScreenBelow?: Breakpoint | number
  bodyRef?: Ref<HTMLDivElement>
  /**
   * Replaces the single scroll body with a main column and a reference rail.
   * When set, `children` is ignored.
   */
  split?: { main: ReactNode; rail: ReactNode }
  /** Set when a request is in flight so a stray backdrop click cannot dismiss it. */
  disableDismiss?: boolean
}

export default function DialogShell({
  open,
  onClose,
  overline,
  title,
  subtitle,
  extra,
  extraOwnRowOnPhone,
  mono,
  belowHeader,
  dock,
  children,
  footer,
  footerStart,
  maxWidth = 'sm',
  width,
  compact,
  closeLabel,
  fullScreenBelow = 'sm',
  bodyRef,
  split,
  disableDismiss,
}: DialogShellProps) {
  const theme = useTheme()
  const titleId = useId()
  const isPhone = useMediaQuery(theme.breakpoints.down(fullScreenBelow))
  const fullScreen = !compact && isPhone
  const bottomSheet = compact && isPhone

  return (
    <Dialog
      open={open}
      onClose={disableDismiss ? undefined : onClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth={width ? false : maxWidth}
      aria-labelledby={titleId}
      sx={bottomSheet ? { '& .MuiDialog-container': { alignItems: 'flex-end' } } : undefined}
      PaperProps={{
        sx: {
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'background.paper',
          color: 'text.primary',
          maxHeight: fullScreen ? '100%' : bottomSheet ? '92%' : '90vh',
          ...(width && !fullScreen && !bottomSheet && { maxWidth: width }),
          ...(bottomSheet && { m: 0, width: '100%', maxWidth: '100%', borderRadius: '18px 18px 0 0' }),
          ...(fullScreen && { borderRadius: 0 }),
        },
      }}
    >
      <ShellHeader
        overline={overline}
        title={title}
        subtitle={subtitle}
        extra={extra}
        extraOwnRowOnPhone={extraOwnRowOnPhone}
        mono={mono}
        onClose={onClose}
        closeLabel={closeLabel}
        titleId={titleId}
      />
      {belowHeader}
      {dock && (
        <Box sx={{ px: PHONE_PX, pt: 1.5, pb: 0.5, flexShrink: 0 }}>{dock}</Box>
      )}
      {split ? (
        <SplitBody main={split.main} rail={split.rail} bodyRef={bodyRef} />
      ) : (
        <ShellBody bodyRef={bodyRef}>{children}</ShellBody>
      )}
      {(footer || footerStart) && <ShellFooter start={footerStart}>{footer}</ShellFooter>}
    </Dialog>
  )
}
